import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SignInButton, SignOutButton, SignUpButton, useAuth, useUser } from '@clerk/clerk-react';
import { colors } from './theme';
import { normalizeRoleToken } from './roles';

const IFRAME_PATH = '/wave-configurator-app/index.html';
const SHELL_SOURCE = 'wave-configurator-shell';
const APP_SOURCE = 'wave-configurator-app';
const DEFAULT_STATUS = { tone: 'info', text: '' };
const WAVE_API_BASE = resolveWaveApiBase();
const SALES_EMAIL = 'sales@northinvent.com';

function buildMailTextFromPayload(payload) {
  const sep = '========================================';
  const lines = [
    '------',
    'WAVE II CONFIGURATION REQUEST',
    sep,
    'CONTACT INFORMATION',
    'Name: ' + (payload.name || ''),
    'E-mail: ' + (payload.email || ''),
    'Project: ' + (payload.project || ''),
  ];
  if (payload.comments) lines.push('Comments: ' + payload.comments);

  lines.push(sep, 'CONFIGURATIONS', sep);

  (payload.configs || []).forEach((config, index) => {
    lines.push('Configuration ' + (index + 1));
    if (config.productLabel) {
      lines.push('Product: ' + config.productLabel + (config.configurationLabel ? ' - ' + config.configurationLabel : ''));
    }
    if (config.sizeLabel) lines.push('Screen: ' + config.sizeLabel);
    if (config.mountLabel) lines.push('Mounting: ' + config.mountLabel);
    if (config.hmiLabel) lines.push('HMI: ' + config.hmiLabel);
    if (config.bondingLabel) lines.push('Bonding: ' + config.bondingLabel);
    if (config.nvisLabel) lines.push('NVIS: ' + config.nvisLabel);
    if (config.systemLabel) lines.push('System: ' + config.systemLabel);
    if (config.width && config.height) {
      lines.push('Dimensions: ' + config.width + '\u00d7' + config.height + (config.depth ? '\u00d7' + config.depth : '') + ' mm');
    }
    if (config.cutWidth && config.cutHeight) {
      lines.push('Cut-out: ' + config.cutWidth + '\u00d7' + config.cutHeight + ' mm');
    }
    const allPns = [];
    if (config.productPn) {
      allPns.push(config.productPn);
    } else if (config.productKey === 'wave-smart') {
      allPns.push('(product P/N not matched \u2014 please verify configuration)');
    }
    (config.parts || []).forEach(p => { if (p.pn) allPns.push(p.pn); });
    if (allPns.length) lines.push('Part Numbers: ' + allPns.join(', '));
    lines.push('Quantity: ' + config.quantity);
    lines.push(sep);
  });

  lines.push('SPECIAL PRODUCT REQUIREMENTS', sep);
  lines.push(payload.specialReqs || '(none)');
  lines.push(sep);
  lines.push('Please provide pricing and delivery information.');
  lines.push('Thank you.');
  lines.push('');
  return lines.join('\n');
}

function collectRoles(user) {
  const seen = new Set();
  const push = value => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(push);
      return;
    }
    if (typeof value === 'string') {
      const normalized = normalizeRoleToken(value);
      if (normalized) {
        seen.add(normalized);
      }
      return;
    }
    if (typeof value === 'object') {
      push(value.roles);
      push(value.role);
    }
  };

  push(user?.publicMetadata);
  push(user?.privateMetadata);
  push(user?.unsafeMetadata);
  push(user?.roles);
  push(user?.role);

  return Array.from(seen);
}

export default function WaveConfiguratorPage() {
  const iframeRef = useRef(null);
  const iframeCleanupRef = useRef(null);
  const roleBootstrapRef = useRef(false);
  const objectUrlRef = useRef('');
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeView, setActiveView] = useState('configurator');
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(1500);
  const [bridgeState, setBridgeState] = useState({
    configCount: 0,
    totalUnits: 0,
    canSave: false,
    isBusy: false,
    project: '',
    isCategoryScreen: true,
    category: '',
  });
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [saveBusy, setSaveBusy] = useState(false);
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [roleSyncState, setRoleSyncState] = useState('');
  const [guestPromptDismissed, setGuestPromptDismissed] = useState(false);
  const [mailModal, setMailModal] = useState({ open: false, subject: '', body: '', copyState: 'idle', hint: null, hintWarn: false });
  const [mailFetching, setMailFetching] = useState(false);

  const roles = useMemo(() => collectRoles(user), [user]);
  const primaryRole = roles[0] || 'guest';
  const subtitle = isSignedIn
    ? `${user?.emailAddresses?.[0]?.emailAddress || 'Signed in'} · role: ${primaryRole}`
    : 'Public access enabled';

  const saveUnavailableReason = activeView !== 'configurator'
    ? 'Return to the configurator view to save the current setup.'
    : !iframeLoaded
      ? 'The configurator is still loading.'
      : bridgeState.isBusy
        ? 'The configurator is still preparing your request.'
        : !bridgeState.canSave
          ? 'Add at least one configured line item before saving.'
          : '';
  const saveDisabled = saveBusy || Boolean(saveUnavailableReason);

  const setBanner = useCallback((tone, text) => {
    setStatus({ tone, text });
  }, []);

  const sendUserContextToIframe = useCallback(() => {
    const target = iframeRef.current?.contentWindow;
    if (!target) return;

    const metadataName = typeof user?.username === 'string'
      ? user.username
      : typeof user?.unsafeMetadata?.username === 'string'
      ? user.unsafeMetadata.username
      : typeof user?.publicMetadata?.username === 'string'
        ? user.publicMetadata.username
        : typeof user?.publicMetadata?.name === 'string'
          ? user.publicMetadata.name
          : '';
    const fallbackName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    const name = metadataName || user?.fullName || fallbackName;
    const email = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress || '';

    target.postMessage(
      {
        source: SHELL_SOURCE,
        type: 'wave-configurator:user-context',
        payload: {
          name,
          email,
        },
      },
      window.location.origin
    );
  }, [user]);

  const syncIframeHeight = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!doc) return;

    const shell = doc.querySelector('.site-shell');
    const categoryScreen = doc.querySelector('.category-screen');
    let nextHeight = 0;

    // When category screen is visible, measure it instead of the hidden site-shell
    if (categoryScreen) {
      const cs = doc.defaultView?.getComputedStyle(categoryScreen);
      if (cs?.display !== 'none') {
        nextHeight = Math.ceil(categoryScreen.scrollHeight);
      }
    }

    if (!nextHeight && shell) {
      const styles = doc.defaultView?.getComputedStyle(shell);
      const marginTop = Number.parseFloat(styles?.marginTop || '0') || 0;
      const marginBottom = Number.parseFloat(styles?.marginBottom || '0') || 0;
      nextHeight = Math.ceil(shell.getBoundingClientRect().height + marginTop + marginBottom);
    }

    if (!nextHeight) {
      const body = doc.body;
      const root = doc.documentElement;
      nextHeight = Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        root?.scrollHeight || 0,
        root?.offsetHeight || 0
      );
    }

    if (nextHeight > 0) {
      setIframeHeight(nextHeight);
    }
  }, []);

  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
    syncIframeHeight();
    sendUserContextToIframe();

    iframeCleanupRef.current?.();

    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    const win = iframe?.contentWindow;
    if (!doc || !win) return;

    const shell = doc.querySelector('.site-shell') || doc.body || doc.documentElement;
    const categoryScreen = doc.querySelector('.category-screen');
    const observer = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => syncIframeHeight())
      : null;

    if (observer && shell) observer.observe(shell);
    if (observer && categoryScreen) observer.observe(categoryScreen);

    win.addEventListener('resize', syncIframeHeight);

    iframeCleanupRef.current = () => {
      observer?.disconnect();
      win.removeEventListener('resize', syncIframeHeight);
    };
  }, [sendUserContextToIframe, syncIframeHeight]);

  const loadSavedConfigurations = useCallback(async () => {
    if (!isSignedIn) {
      setSavedConfigs([]);
      setSelectedConfigId('');
      return;
    }

    setSavedLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('Missing authentication token.');
      }

      const response = await fetch(`${WAVE_API_BASE}/wave-configurations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(await readErrorResponse(response, `Failed to load saved configurations (${response.status}).`));
      }

      const data = await response.json();
      const configs = Array.isArray(data?.configurations) ? data.configurations : [];
      setSavedConfigs(configs);
      setSelectedConfigId(current => {
        if (current && configs.some(config => config.id === current)) {
          return current;
        }
        return configs[0]?.id || '';
      });
    } catch (err) {
      console.error('Failed to load saved configurations', err);
      setBanner('error', err.message || 'Unable to load saved configurations right now.');
    } finally {
      setSavedLoading(false);
    }
  }, [getToken, isSignedIn, setBanner]);

  const persistSavedConfiguration = useCallback(async ({ payload, filename, pdfBase64 }) => {
    if (!isSignedIn) {
      setSaveBusy(false);
      setBanner('warning', 'You are not logged in, please log in to get all the features such as storing your configurations.');
      return;
    }

    try {
      const token = await getToken();
      if (!token) {
        throw new Error('Missing authentication token.');
      }

      const response = await fetch(`${WAVE_API_BASE}/wave-configurations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ payload, filename, pdfBase64 }),
      });
      if (!response.ok) {
        throw new Error(await readErrorResponse(response, `Failed to save configuration (${response.status}).`));
      }
      const data = await response.json().catch(() => null);

      const saved = data?.configuration;
      if (saved) {
        setSavedConfigs(current => [saved, ...current.filter(item => item.id !== saved.id)]);
        setSelectedConfigId(saved.id);
      }
      setActiveView('saved');
      setDrawerOpen(false);
      setBanner('success', 'Configuration saved. Open the hamburger menu to browse saved PDFs.');
    } catch (err) {
      console.error('Failed to save wave configuration', err);
      setBanner('error', err.message || 'Unable to save configuration.');
    } finally {
      setSaveBusy(false);
    }
  }, [getToken, isSignedIn, setBanner]);

  useEffect(() => {
    const handleMessage = event => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.source !== APP_SOURCE) return;

      if (data.type === 'wave-configurator:state') {
        setBridgeState({
          configCount: Number(data.payload?.configCount) || 0,
          totalUnits: Number(data.payload?.totalUnits) || 0,
          canSave: Boolean(data.payload?.canSave),
          isBusy: Boolean(data.payload?.isBusy),
          project: typeof data.payload?.project === 'string' ? data.payload.project : '',
          isCategoryScreen: Boolean(data.payload?.isCategoryScreen),
          category: typeof data.payload?.category === 'string' ? data.payload.category : '',
        });
        return;
      }

      if (data.type === 'wave-configurator:save-ready') {
        void persistSavedConfiguration(data.payload || {});
        return;
      }

      if (data.type === 'wave-configurator:save-error') {
        setSaveBusy(false);
        setBanner('error', data.payload?.message || 'Unable to prepare this configuration for saving.');
        return;
      }

      if (data.type === 'wave-configurator:resize') {
        const h = Number(data.payload?.height);
        if (h > 0) setIframeHeight(h);
        return;
      }

      if (data.type === 'wave-configurator:scroll-to-top') {
        window.scrollTo({ top: 0, behavior: 'instant' });
        return;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [persistSavedConfiguration, setBanner]);

  useEffect(() => {
    if (!isSignedIn || roleBootstrapRef.current) return;
    roleBootstrapRef.current = true;

    (async () => {
      try {
        const token = await getToken();
        if (!token) return;

        const response = await fetch(`${WAVE_API_BASE}/wave-configurations/customer-role`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          setRoleSyncState('pending');
          return;
        }

        const data = await response.json().catch(() => null);
        if (data?.updated && typeof user?.reload === 'function') {
          await user.reload();
        }
        setRoleSyncState(data?.role || 'customer');
      } catch (err) {
        console.warn('Customer role bootstrap failed', err);
        setRoleSyncState('pending');
      }
    })();
  }, [getToken, isSignedIn, user]);

  useEffect(() => {
    if (!iframeLoaded) return;
    sendUserContextToIframe();
  }, [iframeLoaded, sendUserContextToIframe]);

  useEffect(() => {
    if (isSignedIn) {
      setGuestPromptDismissed(false);
    }
  }, [isSignedIn]);

  useEffect(() => {
    if (activeView !== 'configurator') {
      iframeCleanupRef.current?.();
      iframeCleanupRef.current = null;
      setIframeLoaded(false);
    }
  }, [activeView]);

  useEffect(() => {
    if (activeView === 'saved' && isSignedIn) {
      void loadSavedConfigurations();
    }
  }, [activeView, isSignedIn, loadSavedConfigurations]);

  useEffect(() => {
    if (!isSignedIn || !selectedConfigId) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = '';
      }
      setPdfUrl('');
      return;
    }

    let cancelled = false;

    (async () => {
      setPdfLoading(true);
      try {
        const token = await getToken();
        if (!token) {
          throw new Error('Missing authentication token.');
        }

        const response = await fetch(`${WAVE_API_BASE}/wave-configurations/${selectedConfigId}/pdf`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(await readErrorResponse(response, `Failed to load PDF (${response.status}).`));
        }

        const blob = await response.blob();
        if (cancelled) return;

        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
        }
        objectUrlRef.current = URL.createObjectURL(blob);
        setPdfUrl(objectUrlRef.current);
      } catch (err) {
        console.error('Failed to load saved configuration PDF', err);
        if (!cancelled) {
          setBanner('error', err.message || 'Unable to load the selected PDF right now.');
          setPdfUrl('');
        }
      } finally {
        if (!cancelled) {
          setPdfLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getToken, isSignedIn, selectedConfigId, setBanner]);

  useEffect(() => {
    return () => {
      iframeCleanupRef.current?.();
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  const handleSaveCurrent = () => {
    if (!isSignedIn) {
      setBanner('warning', 'You are not logged in, please log in to get all the features such as storing your configurations.');
      return;
    }

    if (saveUnavailableReason || !iframeRef.current?.contentWindow) {
      setBanner('error', saveUnavailableReason || 'The configurator is not ready yet.');
      return;
    }

    setSaveBusy(true);
    setBanner('info', 'Preparing a branded PDF for saving...');
    iframeRef.current.contentWindow.postMessage(
      {
        source: SHELL_SOURCE,
        type: 'wave-configurator:request-save',
      },
      window.location.origin
    );
  };

  const openSavedView = () => {
    setActiveView('saved');
    setDrawerOpen(false);
  };

  const openConfiguratorView = () => {
    setActiveView('configurator');
    setDrawerOpen(false);
  };

  const handleOpenMailModal = async () => {
    if (!selectedConfigId) return;
    setMailFetching(true);
    try {
      const token = await getToken();
      const response = await fetch(`${WAVE_API_BASE}/wave-configurations/${selectedConfigId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Failed to load configuration (${response.status}).`);
      const data = await response.json();
      const payload = data?.configuration?.payload;
      if (!payload) throw new Error('Configuration payload missing.');
      const subject = `Wave II Configuration Request - ${payload.project || 'Untitled'}`;
      const body = buildMailTextFromPayload(payload);
      setMailModal({ open: true, subject, body, copyState: 'idle', hint: null, hintWarn: false });
    } catch (err) {
      setBanner('error', err.message || 'Unable to prepare the mail request.');
    } finally {
      setMailFetching(false);
    }
  };

  const closeMailModal = () => setMailModal(m => ({ ...m, open: false }));

  const setMailHint = (text, warn = false) =>
    setMailModal(m => ({ ...m, hint: text, hintWarn: warn }));

  const openMailUrl = url => {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const sendViaGmail = () => {
    const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(SALES_EMAIL)}&su=${encodeURIComponent(mailModal.subject)}&body=${encodeURIComponent(mailModal.body)}`;
    openMailUrl(url);
    closeMailModal();
    setBanner('info', 'Gmail was opened in a new tab. Review the message and click Send.');
  };

  const sendViaOutlook = () => {
    const url = `https://outlook.live.com/mail/0/deeplink/compose?to=${encodeURIComponent(SALES_EMAIL)}&subject=${encodeURIComponent(mailModal.subject)}&body=${encodeURIComponent(mailModal.body)}`;
    openMailUrl(url);
    closeMailModal();
    setBanner('info', 'Outlook Web was opened in a new tab. Review the message and click Send.');
  };

  const sendViaMailApp = () => {
    const url = `mailto:${encodeURIComponent(SALES_EMAIL)}?subject=${encodeURIComponent(mailModal.subject)}&body=${encodeURIComponent(mailModal.body)}`;
    let handled = false;
    const onBlur = () => { handled = true; };
    window.addEventListener('blur', onBlur, { once: true });
    openMailUrl(url);
    setTimeout(() => {
      window.removeEventListener('blur', onBlur);
      if (handled) {
        closeMailModal();
        setBanner('info', 'Your mail app was opened. Review and send the message from there.');
      } else {
        setMailHint('No mail app responded. Try Gmail or Outlook Web above, or use Copy Text.', true);
      }
    }, 800);
  };

  const copyMailText = () => {
    navigator.clipboard.writeText(mailModal.body).then(() => {
      setMailModal(m => ({ ...m, copyState: 'copied' }));
      setMailHint(`Message copied. Paste it into any mail app addressed to ${SALES_EMAIL}.`, false);
      setTimeout(() => setMailModal(m => ({ ...m, copyState: 'idle' })), 2000);
    }).catch(() => {
      setMailHint('Copy failed — select all text in the box above and copy manually.', true);
    });
  };

  const selectedConfig = savedConfigs.find(config => config.id === selectedConfigId) || null;

  return (
    <div style={{
      ...pageStyleBase,
      background: bridgeState.isCategoryScreen
        ? 'radial-gradient(ellipse 130% 150% at -20% 0%, rgba(50,140,100,0.85) 0%, rgba(50,140,100,0.5) 25%, rgba(50,140,100,0.2) 50%, transparent 68%), radial-gradient(ellipse 130% 150% at 120% 0%, rgba(150,32,32,0.85) 0%, rgba(150,32,32,0.5) 25%, rgba(150,32,32,0.2) 50%, transparent 68%), #020303'
        : bridgeState.category === 'mil'
          ? 'radial-gradient(ellipse 80% 55% at 50% -5%, rgba(192,48,48,0.42) 0%, transparent 70%), radial-gradient(ellipse 55% 40% at 50% 0%, rgba(192,48,48,0.20) 0%, transparent 60%), linear-gradient(180deg, #0d0808 0%, #0a0606 100%)'
          : 'radial-gradient(ellipse 80% 55% at 50% -5%, rgba(70,187,137,0.38) 0%, transparent 70%), radial-gradient(ellipse 55% 40% at 50% 0%, rgba(70,187,137,0.18) 0%, transparent 60%), linear-gradient(180deg, #0a0d0c 0%, #080a09 100%)',
    }} className="wcp-layout">
    <style>{`
      .wcp-layout {
        display: grid;
        grid-template-columns:
          1fr
          min(1180px, calc(100% - 2rem))
          1fr;
        column-gap: 0;
      }
      .wcp-layout > * { grid-column: 2; }
      .wcp-iframe-bleed {
        width: 100vw;
        margin-left: calc(50% - 50vw);
      }
      .wcp-sidebar-col {
        grid-column: 3;
        grid-row: 2 / span 99;
        padding: 1rem 1rem 0 0.75rem;
      }
      .wcp-sidebar-sticky {
        position: sticky;
        top: 5.5rem;
      }
      /* When not enough room for the margin column, drop to bottom inline bar */
      @media (max-width: calc(1180px + 260px + 2rem)) {
        .wcp-sidebar-col {
          grid-column: 2;
          grid-row: auto;
          padding: 0.75rem 0 1.5rem;
        }
        .wcp-sidebar-sticky {
          position: static;
        }
        .wcp-sidebar-sticky > div {
          display: grid;
          grid-template-columns: 1fr 1fr auto auto;
          align-items: center;
          gap: 0.75rem;
        }
        .wcp-sidebar-sticky > div > p {
          display: none;
        }
      }
    `}</style>
      <header style={headerStyle}>
        <div style={headerBrandStyle}>
          <Link to="/" style={brandLinkStyle}>NORTH INVENT</Link>
          <span style={subtitleStyle}>{subtitle.replace('Â·', '-')}</span>
        </div>
        <div style={headerActionsStyle}>
          {isSignedIn ? (
            <>
              <span style={rolePillStyle}>{roleSyncState || primaryRole}</span>
              <SignOutButton>
                <button style={secondaryHeaderButtonStyle}>Sign out</button>
              </SignOutButton>
            </>
          ) : (
            <>
              <SignInButton
                mode="modal"
                forceRedirectUrl="/wave-configurator"
                fallbackRedirectUrl="/wave-configurator"
              >
                <button style={secondaryHeaderButtonStyle}>Log in</button>
              </SignInButton>
              <SignUpButton
                mode="modal"
                forceRedirectUrl="/wave-configurator"
                fallbackRedirectUrl="/wave-configurator"
                unsafeMetadata={{ role: 'customer', roles: ['customer'], signupSource: 'wave-configurator' }}
              >
                <button style={primaryHeaderButtonStyle}>Register</button>
              </SignUpButton>
            </>
          )}
          <button
            type="button"
            onClick={() => setDrawerOpen(open => !open)}
            style={menuButtonStyle}
            aria-label="Open configurator menu"
            aria-expanded={drawerOpen}
          >
            <span style={menuLineStyle} />
            <span style={menuLineStyle} />
            <span style={menuLineStyle} />
          </button>
        </div>
      </header>

      {status.text && (
        <section style={{ ...statusStyle, ...(statusToneStyles[status.tone] || statusToneStyles.info) }}>
          {status.text}
        </section>
      )}

      {!isSignedIn && (
        <section style={guestHintStyle}>
          Guest mode. Log in to save configurations.
        </section>
      )}

      <main style={contentWrapStyle}>
        {activeView === 'configurator' ? (
          <section style={configuratorLayoutStyle}>
            <section style={frameShellStyle} className="wcp-iframe-bleed">
              <iframe
                ref={iframeRef}
                src={IFRAME_PATH}
                title="Wave II Configurator"
                onLoad={handleIframeLoad}
                scrolling="no"
                style={{ ...iframeStyle, height: `${iframeHeight}px` }}
              />
            </section>
          </section>
        ) : (
          <section style={savedShellStyle}>
            {!isSignedIn ? (
              <div style={savedEmptyStyle}>
                <h2 style={{ marginTop: 0 }}>Saved configurations require login</h2>
                <p style={{ marginBottom: '1.25rem', color: 'rgba(255,255,255,0.72)' }}>
                  Sign in or register from this page to store configurations and view the branded PDFs later.
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <SignInButton
                    mode="modal"
                    forceRedirectUrl="/wave-configurator"
                    fallbackRedirectUrl="/wave-configurator"
                  >
                    <button style={secondaryHeaderButtonStyle}>Log in</button>
                  </SignInButton>
                  <SignUpButton
                    mode="modal"
                    forceRedirectUrl="/wave-configurator"
                    fallbackRedirectUrl="/wave-configurator"
                    unsafeMetadata={{ role: 'customer', roles: ['customer'], signupSource: 'wave-configurator' }}
                  >
                    <button style={primaryHeaderButtonStyle}>Register</button>
                  </SignUpButton>
                </div>
              </div>
            ) : (
              <div style={savedGridStyle}>
                <aside style={savedListStyle}>
                  <div style={savedListHeaderStyle}>
                    <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Saved Configurations</h2>
                    <button type="button" onClick={() => void loadSavedConfigurations()} style={listRefreshButtonStyle}>
                      Refresh
                    </button>
                  </div>
                  {savedLoading ? (
                    <p style={mutedParagraphStyle}>Loading saved configurations...</p>
                  ) : savedConfigs.length === 0 ? (
                    <p style={mutedParagraphStyle}>No saved configurations yet. Save one from the configurator view.</p>
                  ) : (
                    savedConfigs.map(config => (
                      <button
                        key={config.id}
                        type="button"
                        onClick={() => setSelectedConfigId(config.id)}
                        style={{
                          ...savedListItemStyle,
                          ...(config.id === selectedConfigId ? savedListItemActiveStyle : null),
                        }}
                      >
                        <strong style={{ display: 'block' }}>{config.project || 'Untitled project'}</strong>
                        <span style={savedItemMetaStyle}>
                          {config.configCount} configs - {config.totalUnits} units
                        </span>
                        <span style={savedItemMetaStyle}>
                          {formatDateTime(config.createdAt)}
                        </span>
                      </button>
                    ))
                  )}
                </aside>

                <section style={pdfViewerShellStyle}>
                  {selectedConfig ? (
                    <>
                      <div style={pdfViewerHeaderStyle}>
                        <div>
                          <h2 style={{ margin: 0 }}>{selectedConfig.project || 'Untitled project'}</h2>
                          <p style={mutedParagraphStyle}>
                            {selectedConfig.contactName || 'No contact'} - {selectedConfig.contactEmail || 'No email'}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '0.65rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={handleOpenMailModal}
                            disabled={mailFetching}
                            style={{ ...sendMailButtonStyle, opacity: mailFetching ? 0.65 : 1, cursor: mailFetching ? 'wait' : 'pointer' }}
                          >
                            {mailFetching ? 'Preparing...' : 'Send Mail Request'}
                          </button>
                          {pdfUrl && (
                            <a href={pdfUrl} download={selectedConfig.filename || 'wave-configuration.pdf'} style={downloadLinkStyle}>
                              Download PDF
                            </a>
                          )}
                        </div>
                      </div>
                      {pdfLoading ? (
                        <p style={mutedParagraphStyle}>Loading PDF preview...</p>
                      ) : pdfUrl ? (
                        <iframe title="Saved Wave Configuration PDF" src={pdfUrl} style={pdfFrameStyle} />
                      ) : (
                        <p style={mutedParagraphStyle}>Select a saved configuration to preview the PDF.</p>
                      )}
                    </>
                  ) : (
                    <div style={savedEmptyStyle}>
                      <h2 style={{ marginTop: 0 }}>No PDF selected</h2>
                      <p style={mutedParagraphStyle}>Choose a saved configuration from the list to preview the branded PDF here.</p>
                    </div>
                  )}
                </section>
              </div>
            )}
          </section>
        )}
      </main>

      {!bridgeState.isCategoryScreen && activeView === 'configurator' && (
        <aside className="wcp-sidebar-col">
          <div className="wcp-sidebar-sticky">
            <div style={summaryCardStyle(bridgeState.category)}>
              <div style={summaryMetricsGridStyle}>
                <div style={summaryMetricTileStyle}>
                  <span style={summaryLabelStyle}>Line Items</span>
                  <strong>{bridgeState.configCount}</strong>
                </div>
                <div style={summaryMetricTileStyle}>
                  <span style={summaryLabelStyle}>Units</span>
                  <strong>{bridgeState.totalUnits}</strong>
                </div>
              </div>
              <button
                type="button"
                onClick={handleSaveCurrent}
                disabled={saveDisabled}
                style={{
                  ...heroActionStyle,
                  padding: '0.82rem 0.95rem',
                  fontSize: '0.95rem',
                  background: bridgeState.category === 'mil' ? '#c03030' : colors.primary,
                  color: bridgeState.category === 'mil' ? '#fff' : '#12211d',
                  opacity: saveDisabled ? 0.65 : 1,
                  cursor: saveDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                {saveBusy ? 'Saving...' : 'Save Current Configuration'}
              </button>
              <p style={summaryHintStyle}>
                {!isSignedIn
                  ? 'Log in after previewing to store configurations on your account.'
                  : saveUnavailableReason || 'Saved configurations stay attached to your account and reopen here as PDFs.'}
              </p>
            </div>
          </div>
        </aside>
      )}

      {!isSignedIn && !guestPromptDismissed && (
        <div style={guestModalBackdropStyle}>
          <div style={guestModalStyle}>
            <button
              type="button"
              onClick={() => setGuestPromptDismissed(true)}
              style={guestModalCloseStyle}
              aria-label="Dismiss login notice"
            >
              ×
            </button>
            <h2 style={guestModalTitleStyle}>Save requires login</h2>
            <p style={guestModalCopyStyle}>
              Log in or register to store configurations on your account. You can also continue as a guest and dismiss this message.
            </p>
            <p style={guestModalMutedStyle}>
              If you register from this flow, your account is provisioned for customer access and you stay on this configurator page afterward.
            </p>
            <div style={guestModalActionsStyle}>
              <SignInButton
                mode="modal"
                forceRedirectUrl="/wave-configurator"
                fallbackRedirectUrl="/wave-configurator"
              >
                <button style={secondaryHeaderButtonStyle}>Log in</button>
              </SignInButton>
              <SignUpButton
                mode="modal"
                forceRedirectUrl="/wave-configurator"
                fallbackRedirectUrl="/wave-configurator"
                unsafeMetadata={{ role: 'customer', roles: ['customer'], signupSource: 'wave-configurator' }}
              >
                <button style={primaryHeaderButtonStyle}>Register</button>
              </SignUpButton>
            </div>
          </div>
        </div>
      )}

      {drawerOpen && (
        <>
          <button type="button" onClick={() => setDrawerOpen(false)} style={drawerBackdropStyle} aria-label="Close menu" />
          <aside style={drawerStyle}>
            <p style={drawerEyebrowStyle}>Menu</p>
            <button type="button" onClick={openConfiguratorView} style={drawerItemStyle}>
              Configurator
            </button>
            <button type="button" onClick={openSavedView} style={drawerItemStyle}>
              Saved Configurations
            </button>
            <button type="button" onClick={handleSaveCurrent} style={drawerItemStyle} disabled={saveDisabled}>
              Save Current PDF
            </button>
          </aside>
        </>
      )}

      {mailModal.open && (
        <div style={mailOverlayStyle} onClick={e => { if (e.target === e.currentTarget) closeMailModal(); }}>
          <div style={mailDialogStyle}>
            <div style={mailDialogHeadStyle}>
              <h2 style={mailDialogTitleStyle}>Mail Request Preview</h2>
              <p style={mailDialogSubStyle}>
                Review and edit the message below, then choose how to send it to <strong>{SALES_EMAIL}</strong>.
              </p>
            </div>
            <div style={mailDialogBodyStyle}>
              <label style={mailLabelStyle} htmlFor="mailSubject">Subject</label>
              <input
                id="mailSubject"
                style={mailInputStyle}
                type="text"
                value={mailModal.subject}
                onChange={e => setMailModal(m => ({ ...m, subject: e.target.value }))}
              />
              <label style={mailLabelStyle} htmlFor="mailBody">Message</label>
              <textarea
                id="mailBody"
                style={mailTextareaStyle}
                value={mailModal.body}
                onChange={e => setMailModal(m => ({ ...m, body: e.target.value }))}
                spellCheck={false}
              />
            </div>
            <div style={mailDialogFootStyle}>
              <div style={mailSendRowStyle}>
                <span style={mailSendViaLabelStyle}>Open with</span>
                <button type="button" style={mailOptionButtonStyle} onClick={sendViaGmail}>Gmail</button>
                <button type="button" style={mailOptionButtonStyle} onClick={sendViaOutlook}>Outlook Web</button>
                <button type="button" style={mailOptionButtonStyle} onClick={sendViaMailApp}>Mail App</button>
              </div>
              <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
                <button type="button" style={secondaryHeaderButtonStyle} onClick={copyMailText}>
                  {mailModal.copyState === 'copied' ? 'Copied!' : 'Copy Text'}
                </button>
                <button type="button" style={secondaryHeaderButtonStyle} onClick={closeMailModal}>Cancel</button>
              </div>
              {mailModal.hint && (
                <p style={{ ...mailHintStyle, ...(mailModal.hintWarn ? mailHintWarnStyle : {}) }}>
                  {mailModal.hint}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return date.toLocaleString();
}

function resolveWaveApiBase() {
  const configured = (process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
  if (configured) {
    return configured;
  }

  if (typeof window === 'undefined') {
    return 'http://localhost:3001/api';
  }

  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3001/api';
  }

  void protocol;
  return '/api';
}

async function readErrorResponse(response, fallbackMessage) {
  const status = response.status;
  try {
    const data = await response.clone().json();
    const detail = data?.message || data?.error || null;
    if (detail) return `[${status}] ${detail}`;
  } catch (_err) {
    // continue
  }

  try {
    const text = (await response.text()).trim();
    if (text && !text.startsWith('<!')) {
      return `[${status}] ${text.replace(/\s+/g, ' ').slice(0, 180)}`;
    }
  } catch (_err) {
    // ignore
  }

  return `[${status}] ${fallbackMessage}`;
}

const pageStyleBase = {
  minHeight: '100vh',
  color: '#f3f7f4',
  padding: 0,
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
  padding: '1rem 1.2rem',
  marginTop: '1.25rem',
  border: '1px solid rgba(147,149,151,0.2)',
  borderRadius: '1.4rem',
  background: 'rgba(20,24,22,0.88)',
  backdropFilter: 'blur(16px)',
};

const headerBrandStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35rem',
};

const brandLinkStyle = {
  color: colors.background,
  textDecoration: 'none',
  fontWeight: 800,
  letterSpacing: '0.08em',
};

const subtitleStyle = {
  color: 'rgba(255,255,255,0.62)',
  fontSize: '0.85rem',
};

const headerActionsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.65rem',
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
};

const rolePillStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.5rem 0.85rem',
  borderRadius: '999px',
  background: 'rgba(70,187,137,0.16)',
  color: colors.primary,
  fontSize: '0.82rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const primaryHeaderButtonStyle = {
  border: 'none',
  borderRadius: '999px',
  padding: '0.7rem 1rem',
  background: colors.primary,
  color: '#12211d',
  fontWeight: 700,
  cursor: 'pointer',
};

const secondaryHeaderButtonStyle = {
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: '999px',
  padding: '0.7rem 1rem',
  background: 'transparent',
  color: colors.background,
  fontWeight: 700,
  cursor: 'pointer',
};

const menuButtonStyle = {
  width: '48px',
  height: '48px',
  borderRadius: '14px',
  border: '1px solid rgba(255,255,255,0.16)',
  background: 'rgba(255,255,255,0.04)',
  display: 'grid',
  alignContent: 'center',
  gap: '4px',
  padding: '0 10px',
  cursor: 'pointer',
};

const menuLineStyle = {
  height: '2px',
  background: colors.background,
  borderRadius: '999px',
};

const summaryCardStyle = (category) => {
  const isMil = category === 'mil';
  return {
    display: 'grid',
    gap: '0.65rem',
    alignContent: 'start',
    padding: '0.9rem',
    borderRadius: '1.1rem',
    background: isMil ? 'rgba(22,8,8,0.96)' : 'rgba(10,24,18,0.96)',
    border: isMil ? '1px solid rgba(180,40,40,0.35)' : '1px solid rgba(70,187,137,0.28)',
    boxShadow: isMil
      ? '0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(180,40,40,0.12)'
      : '0 8px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(70,187,137,0.08)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  };
};

const summaryMetricsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '0.55rem',
};

const summaryMetricTileStyle = {
  display: 'grid',
  gap: '0.25rem',
  padding: '0.65rem',
  borderRadius: '0.9rem',
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.06)',
};

const summaryHintStyle = {
  margin: 0,
  color: 'rgba(255,255,255,0.66)',
  fontSize: '0.82rem',
  lineHeight: 1.5,
};

const summaryLabelStyle = {
  color: 'rgba(255,255,255,0.62)',
};

const heroActionStyle = {
  border: 'none',
  borderRadius: '1rem',
  padding: '0.95rem 1rem',
  background: colors.primary,
  color: '#12211d',
  fontWeight: 800,
  cursor: 'pointer',
};

const statusStyle = {
  marginTop: '1rem',
  padding: '0.95rem 1.1rem',
  borderRadius: '1rem',
  border: '1px solid transparent',
};

const statusToneStyles = {
  info: {
    background: 'rgba(70,187,137,0.1)',
    borderColor: 'rgba(70,187,137,0.24)',
  },
  success: {
    background: 'rgba(70,187,137,0.16)',
    borderColor: 'rgba(70,187,137,0.3)',
  },
  warning: {
    background: 'rgba(255,193,7,0.13)',
    borderColor: 'rgba(255,193,7,0.28)',
  },
  error: {
    background: 'rgba(236,33,36,0.14)',
    borderColor: 'rgba(236,33,36,0.3)',
  },
};

const contentWrapStyle = {
  marginTop: '1rem',
};

const guestHintStyle = {
  marginTop: '0.9rem',
  padding: '0.7rem 0.9rem',
  borderRadius: '999px',
  width: 'fit-content',
  background: 'rgba(236,33,36,0.08)',
  border: '1px solid rgba(236,33,36,0.16)',
  color: 'rgba(255,224,224,0.82)',
  fontSize: '0.8rem',
  letterSpacing: '0.04em',
};

const guestModalBackdropStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 20,
  display: 'grid',
  placeItems: 'center',
  padding: '1.25rem',
  background: 'rgba(7,8,8,0.56)',
  backdropFilter: 'blur(10px)',
};

const guestModalStyle = {
  position: 'relative',
  width: 'min(520px, 100%)',
  padding: '1.35rem',
  borderRadius: '1.35rem',
  background: 'linear-gradient(180deg, rgba(52,20,20,0.96), rgba(26,14,14,0.96))',
  border: '1px solid rgba(236,33,36,0.26)',
  boxShadow: '0 24px 80px rgba(0,0,0,0.38)',
  color: colors.background,
};

const guestModalCloseStyle = {
  position: 'absolute',
  top: '0.8rem',
  right: '0.8rem',
  width: '38px',
  height: '38px',
  borderRadius: '999px',
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.04)',
  color: colors.background,
  fontSize: '1.25rem',
  lineHeight: 1,
  cursor: 'pointer',
};

const guestModalTitleStyle = {
  margin: 0,
  paddingRight: '2.5rem',
  fontSize: '1.45rem',
  lineHeight: 1.1,
};

const guestModalCopyStyle = {
  margin: '0.85rem 0 0',
  color: 'rgba(255,255,255,0.9)',
  lineHeight: 1.6,
};

const guestModalMutedStyle = {
  margin: '0.8rem 0 0',
  color: 'rgba(255,255,255,0.68)',
  lineHeight: 1.55,
  fontSize: '0.94rem',
};

const guestModalActionsStyle = {
  display: 'flex',
  gap: '0.75rem',
  flexWrap: 'wrap',
  marginTop: '1.1rem',
};

const configuratorLayoutStyle = {
  position: 'relative',
};

const frameShellStyle = {
  background: 'transparent',
};


const iframeStyle = {
  width: '100%',
  border: 'none',
  display: 'block',
  background: 'transparent',
};

const savedShellStyle = {
  borderRadius: '1.6rem',
  border: '1px solid rgba(147,149,151,0.18)',
  background: 'rgba(18,22,20,0.94)',
  padding: '1rem',
};

const savedGridStyle = {
  display: 'grid',
  gridTemplateColumns: '320px minmax(0, 1fr)',
  gap: '1rem',
  minHeight: '72vh',
};

const savedListStyle = {
  display: 'grid',
  gap: '0.75rem',
  alignContent: 'start',
};

const savedListHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '0.75rem',
};

const listRefreshButtonStyle = {
  border: '1px solid rgba(255,255,255,0.16)',
  borderRadius: '999px',
  padding: '0.55rem 0.9rem',
  background: 'transparent',
  color: colors.background,
  cursor: 'pointer',
};

const savedListItemStyle = {
  width: '100%',
  textAlign: 'left',
  borderRadius: '1rem',
  border: '1px solid rgba(147,149,151,0.18)',
  background: 'rgba(255,255,255,0.02)',
  padding: '0.95rem',
  color: colors.background,
  cursor: 'pointer',
  display: 'grid',
  gap: '0.35rem',
};

const savedListItemActiveStyle = {
  borderColor: 'rgba(70,187,137,0.46)',
  background: 'rgba(70,187,137,0.12)',
};

const savedItemMetaStyle = {
  color: 'rgba(255,255,255,0.6)',
  fontSize: '0.86rem',
};

const pdfViewerShellStyle = {
  borderRadius: '1.25rem',
  border: '1px solid rgba(147,149,151,0.18)',
  background: 'rgba(8,10,10,0.5)',
  padding: '1rem',
  display: 'grid',
  gap: '1rem',
};

const pdfViewerHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
  flexWrap: 'wrap',
};

const pdfFrameStyle = {
  width: '100%',
  minHeight: '60vh',
  border: 'none',
  borderRadius: '1rem',
  background: '#fff',
};

const savedEmptyStyle = {
  padding: '1rem',
  borderRadius: '1rem',
  border: '1px dashed rgba(147,149,151,0.28)',
};

const mutedParagraphStyle = {
  color: 'rgba(255,255,255,0.68)',
  lineHeight: 1.6,
};

const downloadLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
  borderRadius: '999px',
  padding: '0.75rem 1rem',
  background: colors.primary,
  color: '#12211d',
  fontWeight: 700,
};

const drawerBackdropStyle = {
  position: 'fixed',
  inset: 0,
  border: 'none',
  background: 'rgba(0,0,0,0.45)',
};

const drawerStyle = {
  position: 'fixed',
  top: '1rem',
  right: '1rem',
  width: 'min(320px, calc(100vw - 2rem))',
  padding: '1rem',
  borderRadius: '1.25rem',
  border: '1px solid rgba(147,149,151,0.22)',
  background: 'rgba(16,20,19,0.98)',
  display: 'grid',
  gap: '0.75rem',
  zIndex: 2,
};

const drawerEyebrowStyle = {
  margin: 0,
  color: colors.primary,
  textTransform: 'uppercase',
  letterSpacing: '0.16em',
  fontSize: '0.76rem',
};

const drawerItemStyle = {
  width: '100%',
  textAlign: 'left',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '0.95rem',
  padding: '0.9rem 1rem',
  background: 'rgba(255,255,255,0.02)',
  color: colors.background,
  cursor: 'pointer',
};

const sendMailButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
  border: '1px solid rgba(70,187,137,0.45)',
  borderRadius: '999px',
  padding: '0.75rem 1rem',
  background: 'rgba(70,187,137,0.12)',
  color: colors.primary,
  fontWeight: 700,
  fontSize: '0.88rem',
  letterSpacing: '0.04em',
};

const mailOverlayStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 50,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1.25rem',
  background: 'rgba(5,8,6,0.90)',
  backdropFilter: 'blur(4px)',
};

const mailDialogStyle = {
  background: '#2a2e2c',
  border: '1px solid rgba(70,187,137,0.55)',
  borderRadius: '1.25rem',
  boxShadow: '0 40px 100px rgba(0,0,0,0.8)',
  width: '100%',
  maxWidth: '640px',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const mailDialogHeadStyle = {
  padding: '1.5rem 1.5rem 1rem',
  borderBottom: '1px solid rgba(147,149,151,0.18)',
  background: 'rgba(255,255,255,0.03)',
};

const mailDialogTitleStyle = {
  margin: '0 0 0.5rem',
  fontSize: '1.05rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#fff',
};

const mailDialogSubStyle = {
  margin: 0,
  fontSize: '0.87rem',
  color: 'rgba(255,255,255,0.65)',
  lineHeight: 1.6,
};

const mailDialogBodyStyle = {
  flex: 1,
  overflowY: 'auto',
  padding: '1rem 1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const mailLabelStyle = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.55)',
};

const mailInputStyle = {
  width: '100%',
  padding: '0.6rem 0.85rem',
  background: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(147,149,151,0.28)',
  borderRadius: '0.5rem',
  color: '#f3f7f4',
  fontFamily: 'inherit',
  fontSize: '0.9rem',
  boxSizing: 'border-box',
};

const mailTextareaStyle = {
  width: '100%',
  minHeight: '280px',
  padding: '0.75rem 0.85rem',
  background: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(147,149,151,0.28)',
  borderRadius: '0.5rem',
  color: '#f3f7f4',
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '0.8rem',
  lineHeight: 1.6,
  resize: 'vertical',
  boxSizing: 'border-box',
};

const mailDialogFootStyle = {
  padding: '1rem 1.5rem 1.25rem',
  borderTop: '1px solid rgba(147,149,151,0.18)',
  background: 'rgba(255,255,255,0.03)',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const mailSendRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  flexWrap: 'wrap',
};

const mailSendViaLabelStyle = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.5)',
  marginRight: '0.25rem',
  whiteSpace: 'nowrap',
};

const mailOptionButtonStyle = {
  border: '1px solid rgba(70,187,137,0.35)',
  borderRadius: '0.5rem',
  padding: '0.5rem 0.9rem',
  background: 'rgba(70,187,137,0.1)',
  color: colors.primary,
  fontWeight: 600,
  fontSize: '0.8rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const mailHintStyle = {
  margin: 0,
  fontSize: '0.82rem',
  lineHeight: 1.55,
  padding: '0.6rem 0.85rem',
  borderRadius: '0.5rem',
  background: 'rgba(70,187,137,0.08)',
  border: '1px solid rgba(70,187,137,0.2)',
  color: 'rgba(255,255,255,0.7)',
};

const mailHintWarnStyle = {
  background: 'rgba(236,180,33,0.08)',
  borderColor: 'rgba(236,180,33,0.25)',
  color: '#f5dfa0',
};
